// src/shared/components/ui/ScopeSignalSelector.tsx
// ────────────────────────────────────────────────────────────────────────────
// DRY scope-family signal selector — reusable across all entity contexts.
//
// Works for:
//   • IoT telemetry signals    (temp, hum, rssi)
//   • DaemonOps metric types   (cpu, memory, disk, network, ...)
//   • Scope namespace / keys   (migrator, generic entities)
//   • Shared Links signal pick  (which signals to embed in URL)
//
// Insight: all are scope-family entities — (namespace, key) ≡ signal.
// This component is the ONE canonical multi-select for that concept.
//
// PAC Glass design system — dark glassmorphism, emerald highlights.
// ────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState, useCallback } from 'react'

/* ── Types ─────────────────────────────────────────────────────────── */

export interface ScopeSignalItem {
  /** Unique identifier — scope_key, metric type, device_id, etc. */
  key: string
  /** Display label. */
  label: string
  /** Optional namespace for grouping. */
  ns?: string
  /** Optional colour swatch. */
  color?: string
  /** Optional icon / emoji. */
  icon?: string
  /** Optional description shown below the label. */
  hint?: string
}

export interface ScopeSignalSelectorProps {
  /** All available items to choose from. */
  items: ScopeSignalItem[]
  /** Currently selected keys. */
  selected: Set<string>
  /** Toggle a single item. */
  onToggle: (key: string) => void
  /** Toggle all items at once (select all / deselect all). */
  onToggleAll: () => void
  /** Section title (e.g. "Signals", "Devices", "Scope Keys"). */
  title?: string
  /** Enable the search/filter bar. */
  searchable?: boolean
  /** Compact mode — single row with pills instead of list. */
  compact?: boolean
  /** Max height in px before scrolling (default 280). */
  maxHeight?: number
}

/* ── Component ─────────────────────────────────────────────────────── */

export default function ScopeSignalSelector({
  items,
  selected,
  onToggle,
  onToggleAll,
  title = 'Signals',
  searchable = false,
  compact = false,
  maxHeight = 280,
}: ScopeSignalSelectorProps) {
  const [filter, setFilter] = useState('')

  const filtered = useMemo(() => {
    if (!filter) return items
    const lc = filter.toLowerCase()
    return items.filter(
      it =>
        it.label.toLowerCase().includes(lc) ||
        it.key.toLowerCase().includes(lc) ||
        (it.ns ?? '').toLowerCase().includes(lc),
    )
  }, [items, filter])

  const allSelected = filtered.length > 0 && filtered.every(it => selected.has(it.key))
  const noneSelected = filtered.every(it => !selected.has(it.key))

  /* ── Compact mode: horizontal pill bar ──────────────────────────── */
  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {title && (
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 mr-1">{title}</span>
        )}
        <PillButton
          label={allSelected ? 'None' : 'All'}
          active={allSelected}
          color={undefined}
          onClick={onToggleAll}
        />
        {items.map(it => (
          <PillButton
            key={it.key}
            label={it.icon ? `${it.icon} ${it.label}` : it.label}
            active={selected.has(it.key)}
            color={it.color}
            onClick={() => onToggle(it.key)}
          />
        ))}
      </div>
    )
  }

  /* ── List mode: full panel with checkboxes ──────────────────────── */
  return (
    <div className="pac-glass rounded-lg border border-white/[0.06] overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">{title}</span>
        <button
          onClick={onToggleAll}
          className="text-[10px] text-neutral-500 hover:text-neutral-300 transition-colors"
        >
          {allSelected ? 'Deselect All' : noneSelected ? 'Select All' : 'Toggle All'}
        </button>
      </div>

      {/* Search bar */}
      {searchable && (
        <div className="px-3 py-1.5 border-b border-white/5">
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter…"
            className="w-full px-2 py-1 rounded text-xs text-neutral-200 placeholder-neutral-600
                       pac-glass-input outline-none focus:border-neutral-500"
          />
        </div>
      )}

      {/* Item list */}
      <div className="overflow-y-auto" style={{ maxHeight }}>
        {filtered.length === 0 && (
          <div className="px-3 py-4 text-xs text-neutral-600 text-center">
            {filter ? 'No matches.' : 'No items available.'}
          </div>
        )}
        {filtered.map(it => (
          <SignalRow
            key={it.key}
            item={it}
            checked={selected.has(it.key)}
            onToggle={() => onToggle(it.key)}
          />
        ))}
      </div>

      {/* Footer: selection count */}
      <div className="px-3 py-1.5 border-t border-white/5 text-[10px] text-neutral-600">
        {selected.size} of {items.length} selected
      </div>
    </div>
  )
}

/* ── Sub-components ────────────────────────────────────────────────── */

function SignalRow({
  item,
  checked,
  onToggle,
}: {
  item: ScopeSignalItem
  checked: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-xs transition-colors
        ${checked
          ? 'bg-emerald-500/[0.06] hover:bg-emerald-500/[0.10]'
          : 'hover:bg-white/[0.03]'}`}
    >
      {/* Checkbox */}
      <span
        className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors
          ${checked
            ? 'bg-emerald-600/50 border-emerald-500'
            : 'bg-white/[0.04] border-white/[0.15]'}`}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="text-emerald-300">
            <path d="M2 5.5L4 7.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>

      {/* Color swatch */}
      {item.color && (
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: item.color }}
        />
      )}

      {/* Icon */}
      {item.icon && <span className="shrink-0">{item.icon}</span>}

      {/* Label + hint */}
      <div className="flex-1 min-w-0">
        <div className={`truncate ${checked ? 'text-neutral-200' : 'text-neutral-400'}`}>
          {item.label}
        </div>
        {item.hint && (
          <div className="text-[10px] text-neutral-600 truncate">{item.hint}</div>
        )}
      </div>

      {/* Namespace badge */}
      {item.ns && (
        <span className="text-[9px] text-neutral-600 font-mono shrink-0">{item.ns}</span>
      )}
    </button>
  )
}

function PillButton({
  label,
  active,
  color,
  onClick,
}: {
  label: string
  active: boolean
  color: string | undefined
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium
        border transition-colors
        ${active
          ? 'border-emerald-500/50 bg-emerald-500/[0.12] text-emerald-300'
          : 'border-white/[0.08] bg-white/[0.03] text-neutral-500 hover:text-neutral-300 hover:border-white/[0.15]'}`}
    >
      {color && (
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      )}
      {label}
    </button>
  )
}

/* ── Hook: local Set<string> selection state ──────────────────────────── */

export function useScopeSignalSelection(
  allKeys: string[],
  initialSelected?: string[],
): {
  selected: Set<string>
  toggle: (key: string) => void
  toggleAll: () => void
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>
} {
  const [selected, setSelected] = React.useState<Set<string>>(
    () => new Set(initialSelected ?? allKeys),
  )

  const toggle = useCallback((key: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const allIn = allKeys.every(k => prev.has(k))
      return allIn ? new Set<string>() : new Set(allKeys)
    })
  }, [allKeys])

  return { selected, toggle, toggleAll, setSelected }
}
